import { ElementType } from "react";
import clsx from "clsx";
import { EntityCardProps } from "./types";

export const EntityCard = <T extends ElementType = "div">({
  as,
  title,
  subtitle,
  imageUrl,
  imageAlt = "Card image",
  imageFit = "cover",
  imageFallback = "No Image",
  status,
  chips = [],
  createdAt,
  createdAtLabel = "Created",
  className,
  ...restProps
}: EntityCardProps<T>) => {
  const Component = as || "div";
  const isInteractive = Boolean(as);

  return (
    <Component
      {...restProps}
      className={clsx(
        "bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow flex flex-col",
        "hover:shadow-md",
        isInteractive &&
          "cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
        className,
      )}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={imageAlt}
          className={clsx(
            "w-full h-40 bg-gray-100 rounded-t-lg",
            `object-${imageFit}`,
          )}
        />
      ) : (
        <div className="w-full h-40 bg-gray-100 rounded-t-lg flex items-center justify-center text-gray-400 text-sm">
          {imageFallback}
        </div>
      )}
      <div className="p-3 flex flex-col flex-grow">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{title}</h3>
            {subtitle && (
              <div className="mt-0.5 text-xs text-gray-500 truncate">
                {subtitle}
              </div>
            )}
          </div>
          {status && (
            <span
              className={clsx(
                "inline-flex px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap",
                status.className,
              )}
              style={{ textTransform: "capitalize" }}
            >
              {status.text}
            </span>
          )}
        </div>
        {chips.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {chips.map((chip, chipIndex) => (
              <span
                key={`${chip.label}-${chipIndex}`}
                className="inline-flex items-center gap-1 rounded-md bg-gray-50 px-2 py-1 text-xs"
              >
                <span className="text-gray-500">{chip.label}</span>
                <span className="font-medium text-gray-900 tabular-nums">
                  {chip.value}
                </span>
              </span>
            ))}
          </div>
        )}
        {createdAt && (
          <div className="mt-2 pt-2 text-xs text-gray-500 border-t border-gray-100">
            {createdAtLabel}: <span className="text-gray-700">{createdAt}</span>
          </div>
        )}
      </div>
    </Component>
  );
};
